/**
 * Component for our Automatic Shipping-Labels
 * for users to view, track and resend them.
 *
 * @component
 */

import {
  Badge,
  Button,
  Checkbox,
  Dropdown,
  Input,
  Menu,
  message,
  Popconfirm,
  Popover,
  Tooltip
} from 'antd'
import axios from 'axios'
import React,{ Component } from 'react'
import EditCustomerDetails from './ReturnDetailsCustomer'
import { DownOutlined } from '@ant-design/icons'

class ReturnDetailsLabelAutomatic extends Component {
  constructor(props) {
    super(props)
    const customBoxProfile = props.returnObject?.customBoxProfile || {};
    this.state = {
      loadingRecreateLabel: false,
      recreateLabelPopoverVisible: false,
      loadingBoxOverride: false,
      boxOverrideEnabled: customBoxProfile.enabled || false,
      boxOverrideLength: customBoxProfile.length || '',
      boxOverrideWidth: customBoxProfile.width || '',
      boxOverrideHeight: customBoxProfile.height || '',
      boxOverrideTareWeight: customBoxProfile.tareWeight || '',
    }
  }
  recreatePrepaidLabel = async (
    returnObject,
    { skipAddressValidation = false } = {}
  ) => {
    try {
      this.setState({
        loadingRecreateLabel: true,
        recreateLabelPopoverVisible: false,
      })

      const response = await axios.post('/api/returns/recreateLabel',{
        returnObject,
        skipAddressValidation,
      })

      if (response.data.status === 'error') {
        this.setState({
          loadingRecreateLabel: false,
        })

        return message.error(
          'Error while attempting to create a new label. Try again.',
          4
        )
      }

      this.setState({
        loadingRecreateLabel: false,
      })
      this.props.handleGetReturn()

      return message.success('Label created successfully.',5)
    } catch (err) {
      this.setState({
        loadingRecreateLabel: false,
      })
      return message.error(
        'Error while attempting to create a new label. Try again.',
        2
      )
    }
  };

  handleRecreateLabelPopoverChange = (visible) => {
    if (!this.state.loadingRecreateLabel) {
      this.setState({
        recreateLabelPopoverVisible: visible,
      })
    }
  };

  renderRecreateLabelConfirmation = () => {
    return (
      <div>
        <div style={{ marginBottom: 10 }}>
          Create a new label and send it to the customer?
        </div>
        <div style={{ display: 'flex',gap: 8,justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={() =>
              this.setState({ recreateLabelPopoverVisible: false })
            }
            disabled={this.state.loadingRecreateLabel}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={() =>
              this.recreatePrepaidLabel(this.props.returnObject)
            }
            loading={this.state.loadingRecreateLabel}
            disabled={this.state.loadingRecreateLabel}
          >
            Yes
          </Button>
          <Button
            size="small"
            type="primary"
            danger
            onClick={() =>
              this.recreatePrepaidLabel(this.props.returnObject,{
                skipAddressValidation: true,
              })
            }
            loading={this.state.loadingRecreateLabel}
            disabled={this.state.loadingRecreateLabel}
          >
            Yes, and disable address verification
          </Button>
        </div>
      </div>
    )
  };

  renderRecreateLabelButton = () => {
    const button = (
      <Button
        style={{
          padding: 'var(--input-padding)',
          width: '100%',
        }}
        size="small"
        disabled={this.state.loadingRecreateLabel}
        loading={this.state.loadingRecreateLabel}
      >
        Create new label
      </Button>
    )

    if (this.props.labelService === 'shippo') {
      return (
        <Popover
          content={this.renderRecreateLabelConfirmation()}
          trigger="click"
          open={this.state.recreateLabelPopoverVisible}
          onOpenChange={this.handleRecreateLabelPopoverChange}
        >
          {button}
        </Popover>
      )
    }

    return (
      <Popconfirm
        title="Create a new label and send it to the customer?"
        onConfirm={() =>
          this.recreatePrepaidLabel(this.props.returnObject)
        }
        okText="Yes"
        cancelText="Cancel"
      >
        {button}
      </Popconfirm>
    )
  };

  resendLabelEmail = async (returnObject) => {
    const returnId = returnObject.id
    try {
      const response = await axios.post('/api/returns/resendLabelEmail',{
        returnId,
      })

      const errorStatus = response.data.status === 'error' ? true : false
      if (errorStatus) {
        return message.error(
          'There was an error resending the label. Please check the error message and try again.',
          5
        )
      }

      return message.success('Label sent successfully.',5)
    } catch (err) {
      return message.error(
        'There was an error resending the label. Please check the error message and try again.',
        5
      )
    }
  };

  saveBoxOverride = async () => {
    try {
      this.setState({ loadingBoxOverride: true })
      const response = await axios.post('/api/returns/box-override', {
        returnId: this.props.returnObject.id,
        enabled: this.state.boxOverrideEnabled,
        length: this.state.boxOverrideLength,
        width: this.state.boxOverrideWidth,
        height: this.state.boxOverrideHeight,
        tareWeight: this.state.boxOverrideTareWeight,
      })

      if (response.data?.message) {
        this.setState({ loadingBoxOverride: false })
        return message.error(response.data.message, 5)
      }

      this.setState({ loadingBoxOverride: false })
      return message.success(
        "Box size saved. Click 'Create new label' to apply it.",
        6
      )
    } catch (err) {
      this.setState({ loadingBoxOverride: false })
      const errorMessage = err?.response?.data?.message
      return message.error(
        errorMessage || 'Error saving box size. Try again.',
        5
      )
    }
  };
  render() {
    const menu = [
      {
        key: '1',
        label: <a href={this.props.returnObject.labelLabelUrl} target="_blank">
          Printable Label
        </a>
      }
    ]
    if (this.props.returnObject.labelCommercialInvoiceUrl) {
      menu.push({
        key: '2',
        label: <a
          href={this.props.returnObject.labelCommercialInvoiceUrl}
          target="_blank"
        >
          Show commercial invoice
        </a>
      })
    }
    if (this.props.returnObject.labelQRcodeUrl) {
      menu.push({
        key: '3',
        label: <a
          size="small"
          href={this.props.returnObject.labelQRcodeUrl}
          target="_blank"
        >
          QR-Code Label
        </a>
      })
    }
    return (
      <React.Fragment>
        {this.props.returnObject.labelStatus === 'SUCCESS' ? (
          <React.Fragment>
            <div className="flex-row flex-row--noMargin" style={{ gap: 10 }}>
              <div className="flex-col-xxs-12">
                <div
                  className="flex-row flex-row--noMargin flex-middle-xxs"
                  style={{ gap: 5 }}
                >
                  <strong>Prepaid-label</strong>
                  <Tooltip
                    placement="bottom"
                    title="Label created & sent to customer"
                  >
                    <Badge status="success" />
                  </Tooltip>
                </div>
              </div>
              <div className="flex-col-xxs-12" style={{ padding: 0 }}>
                <div
                  className="flex-row flex-row--noMargin flex-middle-xxs"
                  style={{ rowGap: 10 }}
                >
                  {this.props.returnObject.labelCommercialInvoiceUrl ||
                    this.props.returnObject.labelQRcodeUrl ? (
                    <div className="flex-col-sm-6 flex-col-xxs-12">
                      <Dropdown menu={{ items: menu }}>
                        <Button
                          size="small"
                          style={{
                            padding: 'var(--input-padding)',
                            width: '100%',
                          }}
                          ghost
                        >
                          Download <DownOutlined />
                        </Button>
                      </Dropdown>
                    </div>
                  ) : (
                    <div className="flex-col-sm-6 flex-col-xxs-12">
                      <Button
                        size="small"
                        href={this.props.returnObject.labelLabelUrl}
                        target="_blank"
                        style={{
                          padding: 'var(--input-padding)',
                          width: '100%',
                        }}
                        ghost
                      >
                        Show label
                      </Button>
                    </div>
                  )}
                  <div className="flex-col-sm-6 flex-col-xxs-12">
                    <Button
                      size="small"
                      href={this.props.returnObject.labelTrackingUrlProvider}
                      target="_blank"
                      style={{ padding: 'var(--input-padding)',width: '100%' }}
                      ghost
                    >
                      Track shipment
                    </Button>
                  </div>
                  <div className="flex-col-sm-6 flex-col-xxs-12">
                    <Popconfirm
                      title="Resend your default email template to the customer that includes the prepaid-label?"
                      onConfirm={() =>
                        this.resendLabelEmail(this.props.returnObject)
                      }
                      okText="Yes"
                      cancelText="Cancel"
                    >
                      <Button
                        size="small"
                        ghost
                        style={{
                          padding: 'var(--input-padding)',
                          width: '100%',
                        }}
                      >
                        Resend label
                      </Button>
                    </Popconfirm>
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        ) : null}
        {this.props.returnObject.labelStatus === 'ERROR' ? (
          <React.Fragment>
            <div className="flex-row flex-row--noMargin" style={{ gap: 10 }}>
              <div className="flex-col-xxs-12">
                <div
                  className="flex-row flex-row--noMargin flex-middle-xxs"
                  style={{ gap: 5 }}
                >
                  <strong>Prepaid-label</strong>
                  <Tooltip placement="bottom" title="Error creating label">
                    <Badge status="error" />
                  </Tooltip>
                </div>
              </div>
              <div className="flex-col-xxs-12" style={{ padding: 0 }}>
                <div
                  className="flex-row flex-row--noMargin flex-middle-xxs"
                  style={{ rowGap: 10 }}
                >
                  <div className="flex-col-sm-6 flex-col-xxs-12">
                    <Tooltip
                      placement="bottom"
                      title={
                        this.props.returnObject.labelErrorTransactionSource
                          ? this.props.returnObject
                            .labelErrorTransactionSource +
                          ': ' +
                          this.props.returnObject.labelErrorTransactionText +
                          ' (label could not be created & sent to customer)'
                          : this.props.returnObject.labelErrorTransactionText +
                          ' (label could not be created & sent to customer)'
                      }
                    >
                      <Button
                        size="small"
                        style={{
                          padding: 'var(--input-padding)',
                          width: '100%',
                        }}
                      >
                        View details
                      </Button>
                    </Tooltip>
                  </div>
                  <div className="flex-col-sm-6 flex-col-xxs-12">
                    <Tooltip
                      placement="bottom"
                      title="This will attempt to generate a new label and send the Approved-Email including the label to the customer"
                    >
                      {this.renderRecreateLabelButton()}
                    </Tooltip>
                  </div>
                  <div className="flex-col-sm-6 flex-col-xxs-12">
                    <EditCustomerDetails
                      buttonText="Update address"
                      returnObject={this.props.returnObject}
                      handleGetReturn={this.props.handleGetReturn}
                    />
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        ) : null}
        {this.props.returnObject.labelRateAmount ? (
          <div className="flex-row flex-row--noMargin">
            <div className="flex-col-xxs-12">
              <div
                className="flex-row flex-row--noMargin"
                style={{ columnGap: 10 }}
              >
                <strong>Actual label cost:</strong>
                {`${this.props.returnObject.labelRateAmount} ${this.props.returnObject.labelRateCurrency}`}
              </div>
            </div>
          </div>
        ) : null}
        {this.props.allowManualBoxOverride && (
          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={this.state.boxOverrideEnabled}
              onChange={(e) => {
                const checked = e.target.checked
                this.setState({ boxOverrideEnabled: checked }, () => {
                  if (!checked) {
                    this.saveBoxOverride()
                  }
                })
              }}
            >
              Use custom box size for this return
            </Checkbox>
            {this.state.boxOverrideEnabled && (
              <div style={{ marginTop: 8 }}>
                <Input
                  addonBefore="Length"
                  value={this.state.boxOverrideLength}
                  onChange={(e) =>
                    this.setState({ boxOverrideLength: e.target.value })
                  }
                />
                <Input
                  style={{ marginTop: '2%' }}
                  addonBefore="Width"
                  value={this.state.boxOverrideWidth}
                  onChange={(e) =>
                    this.setState({ boxOverrideWidth: e.target.value })
                  }
                />
                <Input
                  style={{ marginTop: '2%' }}
                  addonBefore="Height"
                  value={this.state.boxOverrideHeight}
                  onChange={(e) =>
                    this.setState({ boxOverrideHeight: e.target.value })
                  }
                />
                <Input
                  style={{ marginTop: '2%' }}
                  addonBefore="Empty box weight"
                  value={this.state.boxOverrideTareWeight}
                  onChange={(e) =>
                    this.setState({ boxOverrideTareWeight: e.target.value })
                  }
                />
              </div>
            )}
            <Button
              style={{ marginTop: 8 }}
              size="small"
              type="primary"
              loading={this.state.loadingBoxOverride}
              disabled={this.state.loadingBoxOverride}
              onClick={this.saveBoxOverride}
            >
              Save box size
            </Button>
          </div>
        )}
      </React.Fragment>
    )
  }
}

export default ReturnDetailsLabelAutomatic
